import React, { useEffect, useState } from 'react';
import { Box, Button, Select, FormControl, InputLabel, MenuItem } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { exchangeRates, USER_MARKET_COMPARISON_COUNT } from 'src/const';
import { User } from 'src/interfaces/iuser';
import {
  generateDownloadFilename,
  processArrayToCsv,
  processMarketData,
  processReportData,
} from './helper';
import { formatCurrency, makeDownloadableBlob } from 'src/utils/functions';
import TooltipNew from 'src/App/HOC/Tooltips/TooltipNew';

const DownloadButton = ({ children, user }) => {
  if (user.subscriptionPlan !== 'scale') {
    return (
      <TooltipNew
        arrow={false}
        placement="bottom"
        title={
          'Available exclusively on the Scale plan. Export up to 100 market comparisons in total.'
        }
        content={''}
      >
        {children}
      </TooltipNew>
    );
  }

  return children;
};

const MarketDataGrid = ({
  data,
  currency,
  user,
  handleOnSampleClick,
  handleComparisonWrapper,
  comparisons,
  isMobileScreen,
  source,
}: {
  data: any;
  currency: string;
  user: User;
  handleOnSampleClick: any;
  handleComparisonWrapper: any;
  comparisons: { label: string; details: any }[];
  isMobileScreen: boolean;
  source: 'market' | 'report';
}) => {
  const currencySymbol = exchangeRates[currency].symbol;

  const MarketCompareColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 2,
    },
    {
      field: 'occupancy',
      headerName: 'Occupancy (%)',
      type: 'number',
      flex: 1,
      renderCell: params => {
        return params.row.isCacheOutdated ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '10px',
              width: '100%',
            }}
          >
            ⌛ Processing... (~5 min){' '}
            <Button
              variant="contained"
              size="small"
              sx={{ color: '#fff' }}
              onClick={handleComparisonWrapper}
            >
              Check Again
            </Button>
          </Box>
        ) : (
          params.value
        );
      },
      colSpan: params => (params.row.isCacheOutdated ? 4 : 1),
    },
    {
      field: 'adr',
      headerName: `ADR (${currencySymbol})`,
      type: 'number',
      flex: 1,
      renderCell: params => {
        return params.row.isCacheOutdated ? '' : formatCurrency(String(params.value));
      },
      colSpan: () => 0,
    },
    {
      field: 'revenue',
      headerName: `Revenue (${currencySymbol})`,
      type: 'number',
      flex: 1,
      renderCell: params => {
        return params.row.isCacheOutdated ? '' : formatCurrency(String(params.value));
      },
      colSpan: () => 0,
    },
    {
      field: 'listings',
      headerName: 'Listings',
      type: 'number',
      flex: 1,
      renderCell: params => {
        return params.row.isCacheOutdated ? '' : formatCurrency(String(params.value));
      },
      colSpan: () => 0,
    },
  ];

  const ReportCompareColumns = [
    { field: 'name', headerName: 'Name', flex: 2 },
    { field: 'bedrooms', headerName: 'Bedrooms', flex: 1 },
    { field: 'occupancy', headerName: 'Occupancy (%)', type: 'number', flex: 1 },
    { field: 'adr', headerName: `ADR (${currencySymbol})`, type: 'number', flex: 1 },
    { field: 'revenue', headerName: `Revenue (${currencySymbol})`, type: 'number', flex: 1 },
  ];

  const [selectedBedroom, setSelectedBedroom] = useState<string | number>('any');
  const [rowData, setRowData] = useState<any[]>([]);

  useEffect(() => {
    setRowData(
      source === 'market'
        ? processMarketData(data, selectedBedroom, currency, comparisons)
        : processReportData(data, currency)
    );
  }, [data, selectedBedroom, currency]);

  return (
    <div
      id="marketCompareDataTable"
      style={{ height: 420, width: '100%', boxSizing: 'border-box' }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          width: '100%',
          maxWidth: '100%',
          mb: '10px',
          gap: '12px',
          boxSizing: 'border-box',
        }}
      >
        {source === 'market' && (
          <FormControl sx={{ width: '120px' }} size="small">
            <InputLabel>Bedroom</InputLabel>
            <Select
              value={selectedBedroom}
              label="Bedroom"
              onChange={event => setSelectedBedroom(event.target.value)}
            >
              {[
                ['Any', 'any'],
                ['Studio', 0],
                [1, 1],
                [2, 2],
                [3, 3],
                [4, 4],
                [5, 5],
                ['6+', 6],
              ].map(bedroom => (
                <MenuItem value={bedroom[1]}>{bedroom[0]}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <DownloadButton user={user}>
          <Button
            variant="outlined"
            startIcon={<DownloadRoundedIcon />}
            onClick={() => {
              if (user.subscriptionPlan !== 'scale') {
                handleOnSampleClick('scale');
                return;
              }

              if (rowData) {
                const formattedData = rowData.filter(data => {
                  return !data.isCacheOutdated;
                });

                makeDownloadableBlob(
                  processArrayToCsv(formattedData),
                  generateDownloadFilename(),
                  true,
                  true
                );
              }
            }}
          >
            Download
          </Button>
        </DownloadButton>
      </Box>

      {rowData && (
        <Box sx={{ height: isMobileScreen ? '340px' : '100%', overflowX: 'auto' }}>
          <DataGrid
            rows={rowData}
            columns={source === 'market' ? MarketCompareColumns : ReportCompareColumns}
            sx={{
              width: '100%',
              minWidth: '600px',
            }}
          />
        </Box>
      )}
    </div>
  );
};

export default MarketDataGrid;
